import { SearchIcon } from "@cmp-src/icons/icons/SearchIcon";
import { cn } from "@gettactic/helpers";
import { useDebouncedEffect } from "@gettactic/helpers/src/hooks/useDebouncedEffect";
import React, { useCallback, useEffect, useState } from "react";
import { HiX } from "react-icons/hi";

export type SearchInputVariant =
  | "default"
  | "subtle"
  | "settings"
  | "kiosk"
  | "kioskDark";

const searchInputVariants: Record<SearchInputVariant, string> = {
  default: "border-neutral-300 bg-white placeholder-neutral-500",
  subtle:
    "border-neutral-200 bg-gray-50 placeholder-neutral-400 focus:bg-white",
  settings:
    "h-10 rounded-[10px] border-[#dcdde0] bg-white pl-9 pr-3 py-0 font-inter font-normal text-[#161c22] placeholder-neutral-400",
  kiosk:
    "border-neutral-200 bg-white placeholder-neutral-400 text-neutral-900 text-base py-3 px-12 rounded-xl",
  kioskDark:
    "border-white/20 bg-white/10 placeholder-white/50 text-white text-base py-3 px-12 rounded-xl focus:border-white/40 focus:bg-white/15"
};

const searchInputIconVariants: Record<SearchInputVariant, string> = {
  default: "text-neutral-600",
  subtle: "text-neutral-1000",
  settings: "text-neutral-400",
  kiosk: "text-neutral-400",
  kioskDark: "text-white/50"
};

const searchInputClearVariants: Record<SearchInputVariant, string> = {
  default: "text-neutral-400 hover:text-neutral-700",
  subtle: "text-neutral-400 hover:text-neutral-700",
  settings: "text-neutral-400 hover:text-neutral-700",
  kiosk: "text-neutral-400 hover:text-neutral-700",
  kioskDark: "text-white/50 hover:text-white"
};

export interface SearchProps extends React.HTMLAttributes<HTMLInputElement> {
  query: string;
  setQuery:
    | React.Dispatch<React.SetStateAction<string>>
    | ((query: string) => void);
  debounceTimeout?: number;
  name?: string;
  inputId?: string;
  placeholder?: string;
  preventAutoLowerCase?: boolean;
  fullWidth?: boolean;
  withFocusRing?: boolean;
  enableClearButton?: boolean;
  /** Visual variant of the search input */
  variant?: SearchInputVariant;
  icon?: React.ReactNode;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchProps>(
  function Search(props, ref) {
    const {
      query,
      setQuery,
      debounceTimeout = 400,
      placeholder = "Search",
      name = "search",
      inputId = "search",
      preventAutoLowerCase = false,
      fullWidth = false,
      className,
      withFocusRing = true,
      enableClearButton = true,
      icon,
      variant = "default",
      ...other
    } = props;
    // we create a local queryInput to sync state with input
    // so we can call the real one only after a debounced effect
    const [queryInput, setQueryInput] = useState(query);
    useDebouncedEffect(
      () => setQuery(queryInput),
      [queryInput],
      debounceTimeout
    );
    const handleQueryInput: React.ChangeEventHandler<HTMLInputElement> =
      useCallback((e) => {
        const queryString = e.currentTarget.value;
        setQueryInput(
          preventAutoLowerCase ? queryString : queryString.toLowerCase()
        );
      }, []);

    const resetQueryField = useCallback(() => {
      setQuery("");
      setQueryInput("");
    }, []);

    // Keep queryInput synced with parent query
    // to reflect external changes
    useEffect(() => {
      setQueryInput(query);
    }, [query]);

    const hasFilter = query.length > 0;

    return (
      <div className={cn("flex items-center", fullWidth && "w-full")}>
        <label htmlFor={inputId} className="sr-only">
          Search
        </label>
        <div className={cn("relative", fullWidth && "w-full")}>
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {icon || (
              <SearchIcon
                className={cn(
                  "h-5 w-5 shrink-0",
                  searchInputIconVariants[variant]
                )}
              />
            )}
          </div>
          <input
            type="search"
            className={cn(
              "block rounded-brand border shadow-input",
              "px-10 py-2 text-sm",
              variant !== "kioskDark" &&
                "focus:text-neutral-800 focus:placeholder-neutral-400",
              "focus:outline-none",
              "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
              withFocusRing ? "focus:ring-1" : "focus:ring-0",
              searchInputVariants[variant],
              className,
              fullWidth ? "w-full" : undefined
            )}
            placeholder={placeholder}
            name={name}
            id={inputId}
            value={queryInput}
            onChange={handleQueryInput}
            {...other}
            ref={ref}
            onKeyDown={(event) => {
              // Prevent form submission on Enter
              if (event.code === "Enter" || event.code === "NumpadEnter") {
                event.preventDefault();
              }
              // Call custom onKeyDown if provided
              other.onKeyDown?.(event);
            }}
          />
          {enableClearButton && hasFilter && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <HiX
                className={cn(
                  "h-5 w-5 shrink-0 cursor-pointer",
                  searchInputClearVariants[variant]
                )}
                onClick={resetQueryField}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);
